<template>
  <v-card>
    <v-progress-linear
      :active="loading"
      indeterminate
      absolute
      top
      color="primary"
      height="2"
    />
    <v-card-text class="d-flex pt-5 pb-2 px-5 align-center">
      <v-select
        v-model="selectedGroup"
        :items="groupOptions"
        dense
        outlined
        hide-details
        style="max-width: 220px; flex: 0 0 auto;"
        class="mr-3"
      />
      <v-select
        v-show="false"
        v-model="resolution"
        :items="resolutionOptions"
        dense
        outlined
        hide-details
        style="width: 110px; flex: 0 0 auto;"
        class="mr-3"
      />
      <v-slider
        v-model="timeIndex"
        :min="0"
        :max="Math.max(0, snapshots.length - 1)"
        :step="1"
        :disabled="realtime || !snapshots.length"
        hide-details
        dense
        class="mr-3"
      />
      <div
        class="text-no-wrap text-caption mr-2 d-flex align-center"
        style="min-width: 80px;"
      >
        <span
          v-if="realtime"
          class="live-dot mr-1"
        />
        {{ realtime ? 'LIVE' : snapshotDate }}
      </div>
      <v-btn
        :color="realtime ? 'success' : ''"
        :outlined="!realtime"
        small
        @click="toggleRealtime"
      >
        <v-icon
          left
          small
        >
          mdi-broadcast
        </v-icon>
        Realtime
      </v-btn>
    </v-card-text>

    <v-card-text
      class="px-4 pt-2 pb-4"
      style="height: calc(100vh - 160px);"
    >
      <HeatmapChart
        ref="chart"
        :bubbles="bubbles"
        :x-range="activeConfig.xRange"
        :y-range="activeConfig.yRange"
        :x-step="activeConfig.xStep"
        :y-step="activeConfig.yStep"
        :x-unit="activeConfig.xUnit"
        :x-label="activeConfig.xLabel"
        :y-label="activeConfig.yLabel"
        :zones="activeConfig.zones"
        :x-highlights="activeConfig.xHighlights || []"
        :y-highlights="activeConfig.yHighlights || []"
      />
    </v-card-text>
  </v-card>
</template>

<script>
import Vue from 'vue'
import VueSocketIO from 'vue-socket.io'
import SocketIO from 'socket.io-client'
import HeatmapChart from '../components/HeatmapChart.vue'
import { HEATMAP_GROUPS, getGroupTickers } from '../utils/heatmapGroups'
import { buildSnapshots, formatTradingDate } from '../utils/heatmapMath'
import { applyTick } from '../utils/rawData'
import { HEATMAPS, HEATMAP_LIST } from '../heatmaps'
import { TRADING_HISTORY_API } from '../constants'

Vue.use(new VueSocketIO({
  connection: SocketIO('https://tradingviewrealtime.vps.com.vn', { forceNew: true, transports: ['websocket'] }),
}))

const WINDOW_SECONDS = 500 * 24 * 3600
const EMA_PADDING_SECONDS = 100 * 24 * 3600
const FETCH_RANGE_SECONDS = WINDOW_SECONDS + EMA_PADDING_SECONDS
const rawCache = new Map()

export default {
  components: {
    HeatmapChart,
  },

  data() {
    return {
      selectedGroup: 'VN30',
      resolution: '1D',
      resolutionOptions: [
        { text: '1 ngày', value: '1D' },
        { text: '1 giờ', value: '60' },
        { text: '30 phút', value: '30' },
        { text: '15 phút', value: '15' },
        { text: '5 phút', value: '5' },
      ],
      timeIndex: 0,
      realtime: false,
      liveBubbles: null,
      subscribedTickers: [],
      tickerSeries: [],
      loading: false,
      fetchToken: 0,
    }
  },

  computed: {
    selectedConfigKey() {
      return this.$route.meta.variant === 'adwin' ? HEATMAP_LIST[1].key : HEATMAP_LIST[0].key
    },
    activeConfig() {
      return HEATMAPS[this.selectedConfigKey]
    },
    groupOptions() {
      return Object.keys(HEATMAP_GROUPS).map(k => ({ text: k, value: k }))
    },
    built() {
      const result = buildSnapshots(this.tickerSeries)
      const cutoff = Math.floor(Date.now() / 1000) - WINDOW_SECONDS
      const startIdx = result.times.findIndex(t => t >= cutoff)
      if (startIdx <= 0) return result

      return {
        snapshots: result.snapshots.slice(startIdx),
        times: result.times.slice(startIdx),
      }
    },
    snapshots() {
      return this.built.snapshots
    },
    times() {
      return this.built.times
    },
    bubbles() {
      if (this.realtime && this.liveBubbles) return this.liveBubbles

      const current = this.snapshots[this.timeIndex] || []
      const previous = this.snapshots[this.timeIndex - 1] || []
      const prevMap = new Map()
      previous.forEach(b => prevMap.set(b.symbol, b.y))

      return current.map(b => {
        const prevY = prevMap.get(b.symbol)
        let trending = 0
        if (prevY != null) {
          if (b.y > prevY) trending = 1
          else if (b.y < prevY) trending = -1
        }

        return { ...b, trending }
      })
    },
    snapshotDate() {
      const time = this.times[this.timeIndex]

      return time ? formatTradingDate(time) : ''
    },
  },

  watch: {
    selectedGroup() {
      this.refreshOnConfigChange()
    },
    resolution() {
      this.refreshOnConfigChange()
    },
  },

  sockets: {
    stock(data) {
      this.handleTick(data)
    },
  },

  created() {
    this.fetchData()
  },

  beforeDestroy() {
    this.stopRealtime()
  },

  methods: {
    async refreshOnConfigChange() {
      const wasRealtime = this.realtime
      if (wasRealtime) this.unsubscribeAll()
      await this.fetchData()
      if (wasRealtime) {
        this.resetLive()
        this.subscribeAll()
      }
    },

    async fetchData() {
      this.fetchToken += 1
      const token = this.fetchToken
      this.loading = true

      const tickers = getGroupTickers(this.selectedGroup)
      const series = await Promise.all(tickers.map(symbol => this.fetchSymbol(symbol)))

      if (token !== this.fetchToken) return

      this.tickerSeries = series.filter(s => s && s.t.length)
      this.timeIndex = Math.max(0, this.built.snapshots.length - 1)
      this.loading = false
    },

    async fetchSymbol(symbol) {
      const key = `${symbol}|${this.resolution}`
      if (rawCache.has(key)) {
        return { symbol, ...this.activeConfig.compute(rawCache.get(key)) }
      }

      const to = Math.floor(Date.now() / 1000)
      const from = to - FETCH_RANGE_SECONDS

      try {
        const resp = await this.$http.get(TRADING_HISTORY_API, {
          params: {
            symbol, resolution: this.resolution, from, to,
          },
        })
        if (resp.s === 'no_data' || !resp.data) return null
        rawCache.set(key, resp.data)

        return { symbol, ...this.activeConfig.compute(resp.data) }
      } catch (e) {
        console.error(`fetchSymbol ${symbol} failed`, e)

        return null
      }
    },

    toggleRealtime() {
      if (this.realtime) this.stopRealtime()
      else this.startRealtime()
    },

    startRealtime() {
      this.realtime = true
      this.timeIndex = Math.max(0, this.snapshots.length - 1)
      this.resetLive()
      this.subscribeAll()
    },

    stopRealtime() {
      this.unsubscribeAll()
      this.realtime = false
      this.liveBubbles = null
    },

    subscribeAll() {
      const tickers = getGroupTickers(this.selectedGroup)
      tickers.forEach(s => this.emitSocketAction('join', s))
      this.subscribedTickers = tickers
    },

    unsubscribeAll() {
      this.subscribedTickers.forEach(s => this.emitSocketAction('leave', s))
      this.subscribedTickers = []
    },

    emitSocketAction(action, symbol) {
      this.$socket.emit('regs', JSON.stringify({ action, list: `CHART.${symbol}` }))
    },

    resetLive() {
      if (!this.realtime) return
      const latest = this.snapshots[this.snapshots.length - 1] || []
      const previous = this.snapshots[this.snapshots.length - 2] || []
      const prevMap = new Map()
      previous.forEach(b => prevMap.set(b.symbol, b.y))

      this.liveBubbles = latest.map(b => {
        const prevY = prevMap.get(b.symbol)
        let trending = 0
        if (prevY != null) {
          if (b.y > prevY) trending = 1
          else if (b.y < prevY) trending = -1
        }

        return { ...b, trending }
      })
    },

    handleTick(input) {
      if (!this.realtime || !this.liveBubbles || !input) return
      const { symbol } = input
      if (!symbol) return

      const key = `${symbol}|${this.resolution}`
      const raw = rawCache.get(key)
      if (!raw) return

      const newRaw = applyTick(raw, input, this.resolution)
      if (!newRaw) return
      rawCache.set(key, newRaw)

      const series = this.activeConfig.compute(newRaw)
      const lastIdx = series.t.length - 1
      if (lastIdx < 0) return

      const bubIdx = this.liveBubbles.findIndex(b => b.symbol === symbol)
      if (bubIdx === -1) return

      const prevY = this.liveBubbles[bubIdx].y
      const newY = series.y[lastIdx]
      let trending = 0
      if (!Number.isNaN(prevY)) {
        if (newY > prevY) trending = 1
        else if (newY < prevY) trending = -1
      }

      const next = [...this.liveBubbles]
      next[bubIdx] = {
        symbol,
        x: series.x[lastIdx],
        y: newY,
        trending,
      }
      this.liveBubbles = next
    },
  },
}
</script>

<style scoped>
.live-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #56CA00;
  animation: live-pulse 1.2s ease-in-out infinite;
}

@keyframes live-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.4; transform: scale(0.7); }
}
</style>
